import { Fragment, type ReactNode } from 'react';
import type { SortState } from '@/types';
import { ic } from '@/components/Icons';
import { useIsMobile } from '@/hooks/useIsMobile';

export interface Column<T> {
  key: string;
  label?: ReactNode;
  render?: (row: T) => ReactNode;
  align?: 'left' | 'right' | 'center';
  mono?: boolean;
  muted?: boolean;
  sortable?: boolean;
}

export interface DataTableProps<T> {
  columns: Column<T>[];
  rows: T[];
  onRowClick?: (row: T) => void;
  empty?: ReactNode;
  sort?: SortState | null;
  onSort?: (key: string) => void;
  /** Freezes the header inside the table's own scroll area. */
  stickyHeader?: boolean;
  maxHeight?: string | number;
  /** Contenido desplegado debajo de una fila (p. ej. los sites de una
   *  organización). null = fila cerrada. */
  expanded?: (row: T) => ReactNode | null;
}

type Keyed = { _key?: string } & Record<string, unknown>;

const SortCaret = ({ active, dir }: { active: boolean; dir?: 'asc' | 'desc' | null }) => (
  <span aria-hidden="true" style={{ display: 'inline-flex', flexDirection: 'column', lineHeight: 0, gap: 2, flex: 'none' }}>
    <svg width="8" height="5" viewBox="0 0 8 5">
      <path d="M4 0L8 5H0Z" fill={active && dir === 'asc' ? '#fff' : 'rgba(255,255,255,.5)'} />
    </svg>
    <svg width="8" height="5" viewBox="0 0 8 5">
      <path d="M4 5L0 0H8Z" fill={active && dir === 'desc' ? '#fff' : 'rgba(255,255,255,.5)'} />
    </svg>
  </span>
);

export function DataTable<T extends Keyed>({
  columns, rows, onRowClick, empty, sort, onSort, stickyHeader = false, maxHeight, expanded,
}: DataTableProps<T>) {
  const isMobile = useIsMobile();

  /* mobile: stacked cards — one card per row, label/value pairs */
  if (isMobile) {
    // The header is the first labelled column; unlabelled leading columns
    // (star icons and the like) render as a prefix inside the header row.
    // The action footer is the LAST column, and only when it has no label.
    const last = columns[columns.length - 1];
    const actionCol = columns.length > 1 && !last.label ? last : null;
    const headerCol = columns.find((c) => c !== actionCol && c.label) || columns[0];
    const prefixCols = columns.filter(
      (c) => c !== actionCol && c !== headerCol && !c.label && columns.indexOf(c) < columns.indexOf(headerCol),
    );
    const fieldCols = columns.filter((c) => c !== headerCol && c !== actionCol && !prefixCols.includes(c));

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {rows.length === 0 ? (
          <div className="card" style={{ padding: 26, textAlign: 'center', color: 'var(--text-3)', fontSize: 13 }}>
            {empty || '—'}
          </div>
        ) : (
          rows.map((r, idx) => (
            <div
              key={r._key || idx}
              className="card"
              onClick={() => onRowClick?.(r)}
              style={{ padding: 0, overflow: 'hidden', cursor: onRowClick ? 'pointer' : 'default' }}
            >
              <div
                style={{
                  padding: '12px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  gap: 10, borderBottom: fieldCols.length ? '1px solid var(--line)' : 'none', background: 'var(--surface-2)',
                }}
              >
                <div style={{ minWidth: 0, flex: 1, display: 'flex', alignItems: 'center', gap: 8 }}>
                  {prefixCols.map((c) => {
                    const v = c.render ? c.render(r) : (r[c.key] as ReactNode);
                    return v ? <span key={c.key} style={{ display: 'inline-flex', flexShrink: 0 }}>{v}</span> : null;
                  })}
                  <div style={{ minWidth: 0, flex: 1, fontFamily: headerCol.mono ? 'var(--mono)' : 'inherit', fontWeight: 600, fontSize: 13.5 }}>
                    {headerCol.render ? headerCol.render(r) : (r[headerCol.key] as ReactNode)}
                  </div>
                </div>
                {onRowClick ? <span style={{ color: 'var(--text-3)', display: 'inline-flex', flexShrink: 0 }}>{ic.chevR}</span> : null}
              </div>
              {fieldCols.length ? (
                <div style={{ padding: '10px 14px', display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0,1fr))', gap: '10px 14px' }}>
                  {fieldCols.map((c) => (
                    <div key={c.key} style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 3 }}>
                        {c.label}
                      </div>
                      <div style={{ fontSize: 12.5, color: c.muted ? 'var(--text-2)' : 'var(--text)', fontFamily: c.mono ? 'var(--mono)' : 'inherit', overflowWrap: 'anywhere' }}>
                        {c.render ? c.render(r) : ((r[c.key] as ReactNode) ?? '—')}
                      </div>
                    </div>
                  ))}
                </div>
              ) : null}
              {expanded?.(r) ? (
                <div onClick={(e) => e.stopPropagation()} style={{ borderTop: '1px solid var(--line)', cursor: 'default' }}>
                  {expanded(r)}
                </div>
              ) : null}
              {actionCol ? (
                <div
                  onClick={(e) => e.stopPropagation()}
                  style={{ padding: '10px 14px', borderTop: '1px solid var(--line)', display: 'flex', justifyContent: 'flex-end', gap: 6, flexWrap: 'wrap' }}
                >
                  {actionCol.render ? actionCol.render(r) : null}
                </div>
              ) : null}
            </div>
          ))
        )}
      </div>
    );
  }

  return (
    <div className="card" style={{ overflow: 'hidden' }}>
      <div style={{ overflowX: 'auto', ...(stickyHeader ? { overflowY: 'auto', maxHeight: maxHeight || '62vh' } : {}) }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr>
              {columns.map((c) => {
                const sortable = c.sortable && onSort;
                const active = !!sort && sort.key === c.key;
                return (
                  <th
                    key={c.key}
                    onClick={sortable ? () => onSort(c.key) : undefined}
                    aria-sort={active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : undefined}
                    style={{
                      textAlign: c.align || 'left', padding: '11px 14px',
                      fontSize: 11, fontWeight: 700, color: '#fff',
                      textTransform: 'uppercase', letterSpacing: '.05em',
                      borderBottom: '1px solid var(--tbl-head-d)', background: 'var(--tbl-head)',
                      whiteSpace: 'nowrap', userSelect: 'none',
                      cursor: sortable ? 'pointer' : 'default',
                      position: stickyHeader ? 'sticky' : undefined,
                      top: stickyHeader ? 0 : undefined,
                      zIndex: stickyHeader ? 3 : undefined,
                    }}
                  >
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, justifyContent: c.align === 'right' ? 'flex-end' : 'flex-start' }}>
                      {c.label}
                      {sortable ? <SortCaret active={active} dir={active ? sort.dir : null} /> : null}
                    </span>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={columns.length} style={{ padding: 30, textAlign: 'center', color: 'var(--text-3)' }}>
                  {empty || '—'}
                </td>
              </tr>
            ) : (
              rows.map((r, idx) => {
                const extra = expanded?.(r) ?? null;
                return (<Fragment key={r._key || idx}>
                <tr
                  onClick={() => onRowClick?.(r)}
                  style={{ cursor: onRowClick ? 'pointer' : 'default', background: idx % 2 === 1 ? 'var(--surface-2)' : 'var(--surface)' }}
                  className="data-row"
                >
                  {columns.map((c) => (
                    <td
                      key={c.key}
                      style={{
                        padding: '9px 14px',
                        borderBottom: idx === rows.length - 1 ? 'none' : '1px solid var(--line)',
                        color: c.muted ? 'var(--text-2)' : 'var(--text)',
                        fontFamily: c.mono ? 'var(--mono)' : 'inherit',
                        textAlign: c.align || 'left',
                        verticalAlign: 'middle',
                      }}
                    >
                      {c.render ? c.render(r) : (r[c.key] as ReactNode)}
                    </td>
                  ))}
                </tr>
                {extra ? (
                  <tr>
                    <td colSpan={columns.length} style={{ padding: 0, borderBottom: '1px solid var(--line)', background: 'var(--bg-2)' }}>
                      {extra}
                    </td>
                  </tr>
                ) : null}
                </Fragment>);
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
